import React, { memo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigActions } from 'actions';
import { LookerService } from 'services';
import { getTabDashboards, putDashboards } from 'services/Dashboards';
import { CACHE_DASHBOARD_TAB_KEY } from 'constants/Constants';
import EntityDeleteWithText from 'containers/components/modals/EntityDeleteWithText';

const MetricDashboardDeleteModal = memo(({ onConfirm, onCancel, data }) => {
    const dispatch = useDispatch();
    const activeDashboard = useSelector(
        (state) => state.config?.components?.[CACHE_DASHBOARD_TAB_KEY] || '-1',
    );
    const [labels, setLabels] = useState();

    // Remove Dashboard Tab from Dashboards Entity and set a new Active Dashboard Tab
    const removeDashboardTab = useCallback(() => {
        return getTabDashboards().then((dashboards) => {
            const newDashboards = dashboards.filter((dashboard) => dashboard.id !== data.id);
            if (newDashboards.length === dashboards.length) return Promise.resolve();
            if (newDashboards.length !== dashboards.length) {
                let currentIndex = dashboards.findIndex(
                    (item) => String(item.id) === String(activeDashboard),
                );

                let newActiveDashboard;
                if (newDashboards[currentIndex]) newActiveDashboard = newDashboards[currentIndex];
                else newActiveDashboard = newDashboards[currentIndex - 1];

                return putDashboards(newDashboards)
                    .then(() => {
                        dispatch(
                            ConfigActions.setConfigWeb(
                                CACHE_DASHBOARD_TAB_KEY,
                                newActiveDashboard.id,
                            ),
                        );
                    })
                    .catch();
            }
        });
    }, [data, dispatch, activeDashboard]);

    const innerOnConfirm = useCallback(() => {
        let call = LookerService.deleteDashboard;
        if (data.isDashboardFolder) call = LookerService.deleteFolder;
        if (data.isWidget) call = LookerService.deleteWidget;

        return call(data.id).then(() => {
            removeDashboardTab().finally(() => onConfirm());
        });
    }, [data, onConfirm, removeDashboardTab]);

    useEffect(() => {
        if (!data) return;
        let literals = {
            title: 'label_analytics_pro_delete_folder',
            label: 'label_analytics_pro_delete_folder_explanation',
            advice: 'label_analytics_pro_delete_folder_alert',
        };
        if (data?.isDashboard) {
            literals = {
                title: 'label_analytics_pro_delete_report',
                label: 'label_analytics_pro_delete_dashboard_explanation',
                advice: 'label_analytics_pro_delete_dashboard_alert',
            };
        } else if (data?.isWidget) {
            literals = {
                title: 'label_analytics_pro_delete_widget',
                label: 'label_analytics_pro_delete_widget_explanation',
                advice: 'label_analytics_pro_delete_widget_alert',
            };
        }
        setLabels(literals);
    }, [data]);

    return (
        <EntityDeleteWithText
            isOpen={!!data}
            deleteDashboard={LookerService.deleteDashboard}
            deleteFolder={LookerService.deleteFolder}
            deleteWidget={LookerService.deleteWidget}
            onConfirm={innerOnConfirm}
            onCancel={onCancel}
            {...labels}
        />
    );
});

export default MetricDashboardDeleteModal;
